// ── Buy Presets ── Loadout preset manager for the buy menu.
// Provides 3 save slots per side (T/CT) with default Full Buy, Eco, and Force Buy presets.
// Persists custom presets to localStorage.

export type BuySide = 'T' | 'CT';

export interface BuyPreset {
  name: string;
  /** List of item IDs to purchase (weapon/equipment/grenade IDs matching buy-menu definitions). */
  items: string[];
}

export const PRESET_STORAGE_KEY = 'counter-pump-buy-presets';

/** Price lookup for all purchasable items (mirrors buy-menu.ts definitions). */
const ITEM_PRICES: Record<string, number> = {
  // Pistols
  glock: 200,
  usp: 200,
  p250: 300,
  deagle: 700,
  fiveseven: 500,
  tec9: 500,
  // SMGs
  mp9: 1250,
  mac10: 1050,
  p90: 2350,
  // Rifles
  famas: 2050,
  galil: 1800,
  ak47: 2700,
  m4a4: 3100,
  m4a1s: 2900,
  aug: 3300,
  sg553: 3000,
  // Shotguns
  nova: 1200,
  // Snipers
  awp: 4750,
  ssg08: 1700,
  // Grenades
  he_grenade: 300,
  smoke_grenade: 300,
  flashbang: 200,
  molotov: 400,
  incendiary: 500,
  // Equipment
  kevlar: 650,
  kevlar_helmet: 1000,
  defuse_kit: 400,
};

const ITEM_SUMMARY_NAMES: Record<string, string> = {
  glock: 'GLOCK',
  usp: 'USP',
  p250: 'P250',
  deagle: 'DEAGLE',
  fiveseven: 'FIVE-SEVEN',
  tec9: 'TEC-9',
  mp9: 'MP9',
  mac10: 'MAC-10',
  p90: 'P90',
  famas: 'FAMAS',
  galil: 'GALIL',
  ak47: 'AK',
  m4a4: 'M4',
  m4a1s: 'M4A1-S',
  aug: 'AUG',
  sg553: 'SG553',
  nova: 'NOVA',
  awp: 'AWP',
  ssg08: 'SSG',
  he_grenade: 'HE',
  smoke_grenade: 'smoke',
  flashbang: 'flash',
  molotov: 'molotov',
  incendiary: 'incendiary',
  decoy_grenade: 'decoy',
  kevlar: 'armor',
  kevlar_helmet: 'armor+helm',
  defuse_kit: 'kit',
};

export function summarizePresetLoadout(items: readonly string[]): string {
  if (items.length === 0) return 'No purchases';

  const counts = new Map<string, number>();
  const order: string[] = [];
  for (const item of items) {
    if (!counts.has(item)) order.push(item);
    counts.set(item, (counts.get(item) ?? 0) + 1);
  }

  return order
    .map((item) => {
      const label = ITEM_SUMMARY_NAMES[item] ?? item.replace(/_/g, ' ').toUpperCase();
      const count = counts.get(item) ?? 1;
      return count > 1 ? `${count}x ${label}` : label;
    })
    .join(' + ');
}

/** Default presets for each side. Slot 0 = Full Buy, 1 = Eco, 2 = Force Buy. */
export const DEFAULT_PRESETS: Record<BuySide, BuyPreset[]> = {
  T: [
    { name: 'Full Buy', items: ['ak47', 'kevlar_helmet', 'smoke_grenade', 'flashbang', 'molotov'] },
    { name: 'Eco', items: [] }, // Default pistol (Glock), no purchases
    { name: 'Force Buy', items: ['galil', 'kevlar', 'flashbang'] },
  ],
  CT: [
    { name: 'Full Buy', items: ['m4a4', 'kevlar_helmet', 'smoke_grenade', 'flashbang', 'incendiary', 'defuse_kit'] },
    { name: 'Eco', items: [] }, // Default pistol (USP-S), no purchases
    { name: 'Force Buy', items: ['famas', 'kevlar', 'flashbang'] },
  ],
};

const NUM_SLOTS = 3;

export class BuyPresetManager {
  private presets: Record<BuySide, BuyPreset[]>;

  constructor() {
    this.presets = this.load();
  }

  /** Get all presets for a side. */
  getPresets(side: BuySide): readonly BuyPreset[] {
    return this.presets[side];
  }

  /** Get a single preset by side and slot index. Returns Eco fallback for out-of-bounds. */
  getPreset(side: BuySide, slot: number): BuyPreset {
    const presets = this.presets[side];
    if (slot < 0 || slot >= presets.length) {
      return { name: 'Eco', items: [] };
    }
    return presets[slot];
  }

  /** Calculate the total cost of a preset. */
  getPresetCost(side: BuySide, slot: number): number {
    const preset = this.getPreset(side, slot);
    return this.calculateCost(preset.items);
  }

  /** Calculate cost for a list of item IDs. */
  calculateCost(items: string[]): number {
    let total = 0;
    for (const id of items) {
      total += ITEM_PRICES[id] ?? 0;
    }
    return total;
  }

  /** Save a preset to a slot and persist to localStorage. Invalid slot indices are ignored. */
  savePreset(side: BuySide, slot: number, preset: BuyPreset): void {
    if (slot < 0 || slot >= NUM_SLOTS) return;
    this.presets[side][slot] = { name: preset.name, items: [...preset.items] };
    this.persist();
  }

  /**
   * Get the recommended preset slot index based on available money.
   * Returns 0 (Full Buy), 2 (Force Buy), or 1 (Eco).
   */
  getRecommendedBuy(side: BuySide, money: number): number {
    const fullBuyCost = this.getPresetCost(side, 0);
    const forceBuyCost = this.getPresetCost(side, 2);

    if (money >= fullBuyCost) return 0; // Full Buy
    if (money >= forceBuyCost) return 2; // Force Buy
    return 1; // Eco
  }

  /** Load presets from localStorage, falling back to defaults. */
  private load(): Record<BuySide, BuyPreset[]> {
    try {
      const raw = localStorage.getItem(PRESET_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<BuySide, BuyPreset[]>;
        // Validate structure
        if (
          parsed.T && Array.isArray(parsed.T) && parsed.T.length === NUM_SLOTS &&
          parsed.CT && Array.isArray(parsed.CT) && parsed.CT.length === NUM_SLOTS
        ) {
          return parsed;
        }
      }
    } catch {
      // Corrupted data — fall back to defaults
    }
    return this.cloneDefaults();
  }

  /** Persist current presets to localStorage. */
  private persist(): void {
    try {
      localStorage.setItem(PRESET_STORAGE_KEY, JSON.stringify(this.presets));
    } catch {
      // localStorage full or unavailable — silently ignore
    }
  }

  /** Deep-clone default presets to avoid mutation. */
  private cloneDefaults(): Record<BuySide, BuyPreset[]> {
    return {
      T: DEFAULT_PRESETS.T.map((p) => ({ name: p.name, items: [...p.items] })),
      CT: DEFAULT_PRESETS.CT.map((p) => ({ name: p.name, items: [...p.items] })),
    };
  }
}
